import type { ReactNode } from 'react';
import heroBg from '@/assets/matrix/bg.png';
import productsBg from '@/assets/matrix/gwg-bg.png';
import guide1Video from '@/assets/matrix/guide-1.mp4';
import guide2Video from '@/assets/matrix/guide-2.mp4';
import guide3Video from '@/assets/matrix/guide-3.mp4';
import ability1 from '@/assets/matrix/ability-1.png';
import ability2 from '@/assets/matrix/ability-2.png';
import ability3 from '@/assets/matrix/ability-3.png';
import ability4 from '@/assets/matrix/ability-4.png';
import gmtHero from '@/assets/matrix/gmt-hero.png';
import gmt1 from '@/assets/matrix/gmt-1.png';
import gmt2 from '@/assets/matrix/gmt-2.png';
import gmt3 from '@/assets/matrix/gmt-3.png';
import gmt4 from '@/assets/matrix/gmt-4.png';
import gmt5 from '@/assets/matrix/gmt-5.png';
import gpHero from '@/assets/matrix/gp-hero.png';
import gp1 from '@/assets/matrix/gp-1.png';
import gp2 from '@/assets/matrix/gp-2.png';
import gp3 from '@/assets/matrix/gp-3.png';
import warHero from '@/assets/matrix/war-hero.png';
import war1 from '@/assets/matrix/war-1.png';
import war2 from '@/assets/matrix/war-2.png';
import war3 from '@/assets/matrix/war-3.png';
import gmtVideo from '@/assets/matrix/gmt.mp4';
import gpVideo from '@/assets/matrix/gp.mp4';
import warVideo from '@/assets/matrix/war.mp4';

export const HERO = {
  title: 'AI低龄段产品矩阵',
  image: heroBg,
  subtitle: (
    <>
      专为低龄段孩子打造系统性成长方案，推出爱学AI思维、爱学AI双语、爱学AI人文三大产品，
      <br />
      “有趣点燃内驱、有料构建体系、有爱实现个性化”，
      <br />
      让孩子在思维、语言与人文素养中真正学会思考、敢于表达、爱上阅读。
    </>
  ),
};

export interface FeatureCardData {
  video: string;
  title: string;
  description: string;
}

export const FEATURES = {
  title: 'AI智趣引导，核心三元素',
  subTitle: '有趣激发兴趣，有料构建体系，有爱温暖陪伴',
  cards: [
    {
      video: guide1Video,
      title: '有趣 · 点燃孩子的内驱力',
      description:
        '孩子注意力集中时间短，我们把知识融入他们熟悉的场景、游戏、故事之中，用生动形象的语言活泼有趣的互动形式，点燃孩子们的学习兴趣。',
    },
    {
      video: guide2Video,
      title: '有料 · 打造科学成长体系',
      description:
        '针对低龄段，我们设计出一套符合孩子认知规律，兼具科学性、逻辑性和实用性的成长体系。',
    },
    {
      video: guide3Video,
      title: '有爱 · 构筑个性化精准互动',
      description:
        '通过AI实现大规模个性化精准互动，即时关注、即时回应，让线上拥有线下的互动感和温度，真正实现“以孩子为中心”。',
    },
  ] as FeatureCardData[],
};

export interface AbilityCardData {
  icon: string;
  title: string;
  description: string;
}

export const ABILITIES = {
  title: '四大AI学习能力',
  subTitle: '感知学情断点，决策学习路径，引导自主推导，记忆成长轨迹',
  cards: [
    {
      icon: ability1,
      title: '感知力',
      description: '实时语音交互 + 过程追踪，\n识别孩子的真实理解程度与思维断点。',
    },
    {
      icon: ability2,
      title: '引导力',
      description: '不直接给答案，递进提问驱动孩子自主推导，\n培养独立思考习惯。',
    },
    {
      icon: ability3,
      title: '决策力',
      description: '基于学情数据，自主决策下一步是追问、提示、\n拆解还是迁移。',
    },
    {
      icon: ability4,
      title: '记忆力',
      description: '为每个孩子建立长期学情档案，\n每一次互动都建立在“完全了解你”的基础上。',
    },
  ] as AbilityCardData[],
};

export interface ConceptCardData {
  image: string;
  title: string;
  description: string;
}

export interface ProductData {
  key: string;
  tab: string;
  title: string;
  subtitle: string;
  description: ReactNode;
  hero: string;
  video: string;
  conceptTitle: string;
  concepts: ConceptCardData[];
}

export const PRODUCTS = {
  title: '三大AI产品，赋能素养成长',
  subTitle: '以AI驱动构建覆盖认知发展全路径的智能学科能力生态',
  background: productsBg,
  tabOrder: ['renwen', 'siwei', 'shuangyu'],
  defaultKey: 'renwen',
  items: {
    siwei: {
      key: 'siwei',
      tab: '爱学AI思维',
      title: '爱学AI思维',
      subtitle: 'AI赋能的思维能力培养平台',
      description: (
        <>
          <span>依据低学龄孩子认知发展规律与学习特质，构建了</span>
          覆盖16种核心思维方法、7大数学模块
          <span>
            的完整数学思维培育体系。依托独创GMT教学法（引导思考—构建模型—迁移能力）提供引导式对话的授课体验；让孩子会思考、会应用、会迁移。
          </span>
        </>
      ),
      hero: gmtHero,
      video: gmtVideo,
      conceptTitle: '五大理念, 保障学习效果',
      concepts: [
        {
          image: gmt1,
          title: '真 · 思维体系',
          description: '独创GMT真思维训练体系，双轨学习系统。',
        },
        {
          image: gmt2,
          title: '真 · 思维课堂',
          description: 'GMT教学法，知识方法到思维，让孩子真正学会思考。',
        },
        { image: gmt3, title: '真 · AI导师', description: '集百位大师经验于一身的超级AI导师。' },
        {
          image: gmt4,
          title: '真 · AI互动体验',
          description: '全开麦引导对话练习，动手操作、长式手写等多重学习体验。',
        },
        {
          image: gmt5,
          title: '真 · 个性化学习',
          description: '学习数据记忆、智能评测、难度动态调整、个性化练习。',
        },
      ],
    },
    renwen: {
      key: 'renwen',
      tab: '爱学AI人文',
      title: '爱学AI人文',
      subtitle: '专业级人文素养与读写能力培养平台',
      description: (
        <>
          <span>依据学习者的认知发展规律与兴趣特质，构建了覆盖</span>
          “阅读输入-思辨内化-创作输出”
          <span>
            的完整人文素养培育体系。依托爱学AI大模型，我们提供沉浸式、对话式的深度阅读与写作引导体验，并结合首个教育级高拟真数字人导师，实现如影随形、启发思辨的个性化教学效果。
          </span>
        </>
      ),
      hero: warHero,
      video: warVideo,
      conceptTitle: '三大理念，保障学习效果',
      concepts: [
        {
          image: war1,
          title: 'AI驱动 · 学习闭环',
          description: 'AI引导“追问-思考-表达”闭环，训练信息抓取、逻辑分析、概括与深意体会能力。',
        },
        {
          image: war2,
          title: '多模态 · 深度互动',
          description:
            '融合语音/视频/文本，通过引导式提问和高频反馈拆解思维链，激发主动思考与表达，告别死记硬背。',
        },
        {
          image: war3,
          title: '个性化讲解 · 练习路径',
          description:
            'AI精准捕捉思维断点，千人千面定制补全方案，针对性强化逻辑，每次学习都提升阅读、思辨与表达力。',
        },
      ],
    },
    shuangyu: {
      key: 'shuangyu',
      tab: '爱学AI双语',
      title: '爱学AI双语',
      subtitle: '新课标适配型双语能力培养平台',
      description: (
        <span>
          严格遵循二语习得规律与小学英语学习路径，构建覆盖「地道输入-高效内化-阅读输出」的完整英语能力提升体系。依托爱学
          AI 大模型与大师经验，打造沉浸式、对话式英语学习体验，结合北美原声外教 +
          资深中教Agent协同，实现随时学、开口练、精准提升的个性化学习效果。
        </span>
      ),
      hero: gpHero,
      video: gpVideo,
      conceptTitle: '三大优势，保障学习效果',
      concepts: [
        {
          image: gp1,
          title: '中外导师Agent协同',
          description:
            '外教AI负责地道口语输入和纠音，中教AI负责能力的内化，一课双效，听说读写全面提升。',
        },
        {
          image: gp2,
          title: 'AI全程真互动',
          description:
            '全程开口、实时语音互动，AI 全程引导追问，每一步都让孩子动脑动口，把语言从功课变回沟通工具。',
        },
        {
          image: gp3,
          title: '全程闭环输出',
          description:
            '整个过程遵循“情景导入→开口回答→即时鼓励→引导探究→总结反馈→自适应规划”完整闭环，知识可积累、能力可迁移。',
        },
      ],
    },
  } as Record<string, ProductData>,
};
